import { Fragment, type ReactElement, type ReactNode, type HTMLAttributes } from "react";
import { Button } from "react-aria-components";

import { cn } from "@/shared/lib/cn";
import "./merry-segmented-switch.css";

export interface MerrySegmentedSwitchOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
  isDisabled?: boolean;
}

export interface MerrySegmentedSwitchProps<T extends string> {
  "aria-label": string;
  className?: string;
  isDisabled?: boolean;
  onChange: (value: T) => void;
  options: readonly MerrySegmentedSwitchOption<T>[];
  /** 为单个选项组合弹层等交互，保留原按钮结构。 */
  renderOption?: (option: MerrySegmentedSwitchOption<T>, button: ReactElement<HTMLAttributes<HTMLElement>>) => ReactNode;
  size?: 34 | 48;
  value: T;
}

/** Controlled, single-choice segmented switch. */
export function MerrySegmentedSwitch<T extends string>({
  "aria-label": ariaLabel,
  className,
  isDisabled = false,
  onChange,
  options,
  renderOption,
  size = 34,
  value,
}: MerrySegmentedSwitchProps<T>) {
  return (
    <div
      aria-label={ariaLabel}
      className={cn("merry-segmented-switch", className)}
      data-size={size}
      role="group"
    >
      {options.map((option) => {
        const button = <Button
          aria-pressed={value === option.value}
          className="merry-segmented-switch__option"
          data-selected={value === option.value}
          isDisabled={isDisabled || option.isDisabled}
          onPress={() => onChange(option.value)}
          type="button"
        >
          {option.icon && <span aria-hidden="true" className="merry-segmented-switch__icon">{option.icon}</span>}
          <span>{option.label}</span>
        </Button>;
        return <Fragment key={option.value}>{renderOption ? renderOption(option, button) : button}</Fragment>;
      })}
    </div>
  );
}
